'use client';

import { useCallback, useEffect, useState } from 'react';

import { useDisplayModeStandalone, useIsIOS } from '@/hooks/usePlatformPWA';

// Minimal typed BeforeInstallPromptEvent interface
interface BeforeInstallPromptEvent extends Event {
  readonly platforms?: string[];
  prompt: () => Promise<void>;
  userChoice: Promise<{ outcome: 'accepted' | 'dismissed'; platform: string }>;
}

declare global {
  interface Window {
    __deferredBIP?: BeforeInstallPromptEvent;
  }
}

export const usePWAInstallPrompt = () => {
  const [deferredPrompt, setDeferredPrompt] =
    useState<BeforeInstallPromptEvent | null>(null);
  const isStandalone = useDisplayModeStandalone();
  const isIOS = useIsIOS();

  useEffect(() => {
    // No need to listen if already installed or on iOS (no BIP event on Safari)
    if (isStandalone || isIOS) return;
    // If we already captured the event earlier in this session, reuse it
    if (window.__deferredBIP) {
      setDeferredPrompt(window.__deferredBIP);
      return;
    }

    const handler = (e: Event) => {
      const bip = e as BeforeInstallPromptEvent;
      // Prevent the mini-infobar when cancelable, then save the event for later user gesture
      if (bip.cancelable) bip.preventDefault();
      window.__deferredBIP = bip;
      setDeferredPrompt(bip);
    };

    // Capture only once per session to avoid repeated console warnings on route changes
    window.addEventListener('beforeinstallprompt', handler as EventListener, {
      once: true,
    });

    return () => {
      // no-op cleanup since listener is once: true
    };
  }, [isStandalone, isIOS]);

  const promptInstall = useCallback(async () => {
    if (!deferredPrompt) return null;
    try {
      await deferredPrompt.prompt();
      const choice = await deferredPrompt.userChoice;
      setDeferredPrompt(null);
      return choice;
    } catch {
      setDeferredPrompt(null);
      return null;
    }
  }, [deferredPrompt]);

  return { deferredPrompt, promptInstall } as const;
};
